import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, comics } from '@/mocks/comics';
import type { ComicData } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [comic, setComic] = useState<ComicData | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (id) {
      const found = getComicById(id);
      if (found) {
        setComic(found);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    }
  }, [id]);

  if (notFound) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col items-center justify-center py-20 text-center">
            <div className="w-24 h-24 flex items-center justify-center rounded-full bg-background-100 mb-6">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-800 mb-3">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被下架</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-center py-40">
            <div className="flex items-center gap-3 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              <span className="text-sm">加载中...</span>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (num: number) => {
    if (num >= 10000) return `${(num / 10000).toFixed(1)}万`;
    return num.toString();
  };

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 5);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: `${comic.category}漫画`, path: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>

          {/* Article Detail */}
          <article className="py-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画无删减在线阅读`}
                    title={`${comic.title} - 免费韩漫下拉式阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

                {/* Stats */}
                <div className="flex flex-wrap items-center gap-4 mb-5">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-foreground-100 text-foreground-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong>{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {formatViews(comic.views)} 次阅读
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-heart-line"></i>
                    {formatViews(comic.favorites)} 收藏
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div className="bg-background-100 rounded-lg p-5 mb-6">
                  <h3 className="text-sm font-medium text-foreground-800 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button className="px-8 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    从第一话开始阅读
                  </button>
                  <button className="px-8 py-2.5 bg-background-100 text-foreground-700 border border-background-200 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-1.5"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            {/* Chapter List */}
            <section className="mt-12">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-heading text-foreground-950">章节列表</h2>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    正序
                  </button>
                  <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap cursor-pointer">
                    倒序
                  </button>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                <div className="divide-y divide-background-100">
                  {comic.chapters.map((ch) => (
                    <a
                      key={ch.id}
                      href="#"
                      className="flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                          {ch.number}
                        </span>
                        <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                          {ch.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updatedAt}</span>
                    </a>
                  ))}
                </div>
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section className="mt-16 pb-16">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-heading text-foreground-950">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} showStats={false} />
                  ))}
                </div>
              </section>
            )}
          </article>

          {/* Article Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: `${comic.title} - 韩国漫画无删减下拉式在线阅读`,
                description: comic.description,
                author: {
                  '@type': 'Person',
                  name: comic.author,
                },
                datePublished: comic.createdAt,
                dateModified: comic.updatedAt,
                publisher: {
                  '@type': 'Organization',
                  name: '桃桃漫画网',
                },
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `https://taotao-comic.com/article/${comic.id}`,
                },
                inLanguage: 'zh-CN',
              }),
            }}
          />
        </div>
      </main>

      <Footer />
    </>
  );
}